<template>
  <main-layout>
    <h1 class="title">Ricerca contratti di assistenza</h1>

    <b-field horizontal grouped label="Data scadenza (da/a)">
      <datepicker v-model="endDateRangeAfter"></datepicker>
      <datepicker v-model="endDateRangeBefore"></datepicker>
    </b-field>
    <b-field horizontal label="Prodotto associato">
      <b-select v-if="productTypes" v-model="selectedProductType">
        <option :value="null"></option>
        <option
          v-for="productType in productTypes.results"
          :key="productType.id"
          :value="productType.id"
        >
          {{ productType.code }}
        </option>
      </b-select>
    </b-field>
    <b-field horizontal label="Categoria contratto">
      <b-select v-model="selectedContractCategory">
        <option :value="null"></option>
        <option value="CdA">CdA</option>
        <option value="FW">FW</option>
      </b-select>
    </b-field>

    <b-field grouped horizontal style="margin-bottom: 30px">
      <p class="control">
        <button
          class="button is-success"
          style="margin-right: 15px"
          @click="fetch()"
        >
          Cerca
        </button>
        <button class="button" @click="clean()">Pulisci</button>
      </p>
    </b-field>

    <div style="position: relative; min-height: 400px">
      <b-table
        v-if="results && results.length > 0"
        striped
        :data="results"
        :total="results.count"
        backend-sorting
        :default-sort="['id', 'desc']"
        @sort="onSort"
      >
        <template slot-scope="props">
          <b-table-column
            field="customer_internal_id"
            label="ID cliente"
            width="40"
            sortable
            numeric
          >
            {{ props.row.customer_internal_id }}
          </b-table-column>

          <b-table-column field="customer_name" label="Cliente" sortable>
            <b>
              <router-link
                :to="{
                  name: 'customers.show',
                  params: { id: props.row.customer }
                }"
              >
                {{ props.row.customer_name }}
              </router-link>
            </b>
          </b-table-column>

          <b-table-column
            field="customer_active_support_contracts"
            label="Licenze attive"
            sortable
          >
            {{ props.row.customer_active_support_contracts }}
          </b-table-column>

          <b-table-column field="type_name" label="Tipologia" sortable>
            {{ props.row.type_name }}
          </b-table-column>

          <b-table-column field="product_name" label="Prodotto" sortable>
            {{ props.row.product_name }}
          </b-table-column>

          <b-table-column field="amount" label="Qt." sortable>
            {{ props.row.amount }}
          </b-table-column>

          <b-table-column field="start_date" label="Inizio" sortable>
            {{ props.row.start_date }}
          </b-table-column>

          <b-table-column field="end_date" label="Scadenza" sortable>
            {{ props.row.end_date }}
          </b-table-column>
          
          <b-table-column field="months_of_contact" label="Durata (mesi)" sortable>
            {{ calculateMonths(props.row) }}
          </b-table-column>
        </template>
        <template slot="footer">
          <download-csv
            class="button is-default"
            :data="resultsForExport"
            name="data.csv"
            delimiter=";"
          >
            <b-icon icon="table"></b-icon>
            <span>CSV</span>
          </download-csv>
        </template>
      </b-table>
      <b-loading :is-full-page="false" :active.sync="loading"></b-loading>
    </div>
  </main-layout>
</template>

<script>
import { get, call } from "vuex-pathify"

import MainLayout from "@/layouts/Main"
import SupportContractProxy from "@/proxies/SupportContractProxy"

export default {
  name: "SupportContractsIndex",
  components: {
    MainLayout
  },
  data() {
    return {
      loading: false,
      results: null,
      sortField: "id",
      sortOrder: "desc",
      endDateRangeAfter: null,
      endDateRangeBefore: null,
      selectedProductType: null,
      selectedContractCategory: null
    }
  },
  computed: {
    productTypes: get("productTypes/productTypes"),
    resultsForExport() {
      if (!this.results) return [];
      return this.results.map(contract => {
        // Create a new object without the months_of_contact field (to avoid redundancy)
        const { months_of_contact, ...contractData } = contract;
        
        let calculatedMonths = 0;
        if (contract.start_date && contract.end_date) {
          // Use backend value if available
          if (months_of_contact) {
            calculatedMonths = months_of_contact;
          } else {
            // Calculate months duration, ensuring a minimum of 1 month
            const startDate = this.$moment(contract.start_date);
            const endDate = this.$moment(contract.end_date);
            
            // Get raw months difference
            const monthsDiff = endDate.diff(startDate, 'months', true);
            
            // If dates are in the same month or less than 1 month apart, return 1
            // Otherwise use the calculated value with precision 2
            calculatedMonths = monthsDiff < 1 ? 1 : parseFloat(monthsDiff.toPrecision(2));
          }
        }
        
        return {
          ...contractData,
          months: calculatedMonths
        };
      });
    }
  },
  mounted() {
    this.fetchProductTypes()
  },
  methods: {
    fetchProductTypes: call("productTypes/all"),
    async fetch() {
      this.loading = true
      let params = {
        ordering: `${this.sortOrder == "desc" ? "-" : ""}${this.sortField}`
      }

      if (this.endDateRangeAfter && this.endDateRangeBefore) {
        params.end_date_range_after = this.fixDate(this.endDateRangeAfter)
        params.end_date_range_before = this.fixDate(this.endDateRangeBefore)
      }

      if (this.selectedProductType) {
        params.product_contract__product = this.selectedProductType
      }

      if (this.selectedContractCategory) {
        params.type__category = this.selectedContractCategory
      }

      this.results = await new SupportContractProxy(params).all()

      if (this.results.length === 0) {
        this.results = null
        this.$toast.open({
          message: "Nessun risultato trovato",
          type: "is-warning"
        })
      }

      this.loading = false
    },
    clean() {
      this.endDateRangeAfter = null
      this.endDateRangeBefore = null
      this.selectedProductType = null
      this.results = null
    },
    fixDate(date) {
      return this.$moment(date).format("YYYY-MM-DD")
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order
      this.fetch()
    },
    calculateMonths(contract) {
      let calculatedMonths = 0;
      if (contract.start_date && contract.end_date) {
        // Use backend value if available
        if (contract.months_of_contact) {
          calculatedMonths = contract.months_of_contact;
        } else {
          // Calculate months duration, ensuring a minimum of 1 month
          const startDate = this.$moment(contract.start_date);
          const endDate = this.$moment(contract.end_date);
          
          // Get raw months difference
          const monthsDiff = endDate.diff(startDate, 'months', true);
          
          // If dates are in the same month or less than 1 month apart, return 1
          // Otherwise use the calculated value with precision 2
          calculatedMonths = monthsDiff < 1 ? 1 : parseFloat(monthsDiff.toPrecision(2));
        }
      }
      return calculatedMonths;
    }
  }
}
</script>
